// Rotary knob component used for per-track and metronome volume.
//
// Mouse drag (vertical, document-captured for fast drags), wheel scroll,
// keyboard arrows/Home/End, dblclick-to-reset (1.0). A floating value tooltip
// appears on hover/drag/wheel/key and disappears instantly on mouseleave or
// drag-end. onChange invocations are coalesced via rAF.
//
// Pure DOM helper — no app state, no global imports beyond the value-format
// callback the caller passes in (`formatValue`). Callers wire onChange to
// their volume/IPC pipeline.

import { gainToDbStr } from './audio-math.js';

export function createKnob({
  initial = 1,
  min = 0,
  max = 2,
  resetValue = 1,
  centerDetent = 0,
  disabled = false,
  formatValue = gainToDbStr,
  onChange,
  onCommit,
} = {}) {
  const mk = (cls) => { const d = document.createElement('div'); d.className = cls; return d; };
  const knob = mk('tr-knob');
  knob.tabIndex = 0;
  knob.setAttribute('role', 'slider');
  knob.setAttribute('aria-valuemin', String(min));
  knob.setAttribute('aria-valuemax', String(max));
  knob.setAttribute('aria-valuenow', String(initial));

  if (disabled) {
    knob.classList.add('is-disabled');
    knob.setAttribute('aria-disabled', 'true');
    knob.tabIndex = -1;
  }

  const tick = mk('tr-knob-tick');
  knob.appendChild(tick);

  const tip = mk('tr-knob-tip');
  knob.appendChild(tip);

  let value = initial;
  let isDragging = false;

  // Tooltip state — tip is visible whenever any of these flags is true.
  // Drag flag dominates: while it's set, no other path can hide the tip.
  let _hoverShown = false;

  const refreshTipVisibility = () => {
    const visible = isDragging || _hoverShown;
    tip.classList.toggle('is-visible', visible);
    if (visible) tip.textContent = formatValue(value);
  };
  const showTipHover = () => { _hoverShown = true;  refreshTipVisibility(); };
  const hideTipHover = () => { _hoverShown = false; refreshTipVisibility(); };

  const updateRotation = () => {
    const frac = (value - min) / (max - min);
    const angle = -135 + frac * 270;
    tick.style.transform = `rotate(${angle}deg)`;
    knob.setAttribute('aria-valuenow', String(value));
    // Always update tip text when visible so the readout tracks the value
    // continuously during drag/wheel/keys.
    if (tip.classList.contains('is-visible')) tip.textContent = formatValue(value);
  };
  updateRotation();

  let _pending = null;
  let _rafId = null;
  const flushChange = () => {
    _rafId = null;
    if (_pending != null) { onChange?.(_pending); _pending = null; }
  };
  const queueChange = () => {
    _pending = value;
    // rAF-coalesce when available; in headless test envs without rAF, fall
    // straight through — commit()/the caller flushes _pending synchronously.
    if (_rafId == null && typeof requestAnimationFrame === 'function') {
      _rafId = requestAnimationFrame(flushChange);
    }
  };

  // Settle-point commit — mirrors bw-volume-bar's onChange(live)/onCommit(persist)
  // split. Flush any pending live onChange FIRST (so the engine has the final
  // value), then fire onCommit for the one-shot persist. Called on drag-end and
  // on each discrete adjustment (wheel/key/dblclick). Without this split, callers
  // that persist on every onChange trigger a re-render storm during a drag
  // (the pan-jitter bug — the volume bar avoided it via exactly this contract).
  const commit = () => {
    if (_rafId != null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(_rafId);
    _rafId = null;
    if (_pending != null) { onChange?.(_pending); _pending = null; }
    onCommit?.(value);
  };

  // Snap helper: when the new candidate value falls within ±centerDetent of zero,
  // snap to exactly 0. Applies only to interactive changes (drag/wheel/key); not
  // to programmatic setValue calls (caller intent must not be silently rewritten).
  function applyDetent(candidate) {
    if (centerDetent > 0 && Math.abs(candidate) <= centerDetent) return 0;
    return candidate;
  }

  let dragStartY = null;
  let dragStartValue = null;
  // A press is a click until the hand travels CLICK_SLOP_PX — the same slop
  // bw-volume-bar uses. Below it nothing turns and the release commits
  // nothing: committing an unchanged value made the desktop rebuild the
  // tracks pane, the second click of a double click landed on a new knob, and
  // dblclick never reached the one that had the listener
  // (B-260930-knob-dblclick-reset-broken).
  const CLICK_SLOP_PX = 3;
  let movedSinceDown = false;

  const onDocMove = (e) => {
    if (dragStartY == null) return;
    const dy = dragStartY - e.clientY;
    if (!movedSinceDown) {
      if (Math.abs(dy) < CLICK_SLOP_PX) return;
      movedSinceDown = true;
    }
    const range = max - min;
    const next = dragStartValue + (dy / 200) * range;
    value = applyDetent(Math.max(min, Math.min(max, next)));
    updateRotation();
    queueChange();
  };
  const onDocUp = () => {
    if (dragStartY == null) return;
    dragStartY = null; dragStartValue = null;
    isDragging = false;
    knob.classList.remove('is-dragging');
    document.removeEventListener('mousemove', onDocMove);
    document.removeEventListener('mouseup', onDocUp);
    window.removeEventListener('blur', onDocUp);
    if (movedSinceDown) commit();   // one-shot persist at drag-end; a click commits nothing
    refreshTipVisibility();   // hides instantly unless cursor is hovering knob
  };

  knob.addEventListener('mousedown', (e) => {
    if (disabled || e.button !== 0) return;
    dragStartY = e.clientY;
    dragStartValue = value;
    movedSinceDown = false;
    isDragging = true;
    knob.classList.add('is-dragging');
    refreshTipVisibility();
    document.addEventListener('mousemove', onDocMove);
    document.addEventListener('mouseup', onDocUp);
    // Потерянный mouseup (Cmd+Tab или системный диалог с зажатой кнопкой)
    // оставлял ручку «в руке»: любое следующее движение мыши крутило её
    // (B-260927-fader-quick-regrab-snaps-unity). Потеря фокуса окна — тоже конец жеста.
    window.addEventListener('blur', onDocUp);
    e.preventDefault();
  });

  knob.addEventListener('wheel', (e) => {
    if (disabled) return;
    e.preventDefault();
    const range = max - min;
    const delta = -e.deltaY / 1000 * range;
    value = applyDetent(Math.max(min, Math.min(max, value + delta)));
    updateRotation();
    queueChange();
    commit();                 // discrete adjustment → live + persist
    showTipHover();
  }, { passive: false });

  knob.addEventListener('keydown', (e) => {
    if (disabled) return;
    const step = (max - min) * 0.01;
    let next = value;
    if (e.key === 'ArrowUp' || e.key === 'ArrowRight') next = value + step;
    else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') next = value - step;
    else if (e.key === 'Home') next = min;
    else if (e.key === 'End') next = max;
    else return;
    e.preventDefault();
    value = applyDetent(Math.max(min, Math.min(max, next)));
    updateRotation();
    queueChange();
    commit();                 // discrete adjustment → live + persist
    showTipHover();
  });

  knob.addEventListener('dblclick', () => {
    if (disabled) return;
    value = resetValue;
    updateRotation();
    queueChange();
    commit();                 // reset → live + persist
    showTipHover();
  });

  // Hover/blur — independent of drag state; refreshTipVisibility composes both.
  knob.addEventListener('mouseenter', showTipHover);
  knob.addEventListener('mouseleave', hideTipHover);
  knob.addEventListener('blur', hideTipHover);

  return {
    el: knob,
    setValue(v) {
      value = Math.max(min, Math.min(max, v));
      updateRotation();
    },
    getValue() { return value; },
  };
}
